import { useCallback } from "react";
import { extractMergeTags } from "../../utils/extract-merge-tags";
import { notEmpty } from "../../utils/not-empty";
import type { DesignHuddleCustomisations } from "../message/editor/nodes/design-huddle-node";
import type { ElementProperties } from "./types";
import { useGetDesignHuddleAccessToken } from "./use-design-huddle-access-token";
import { useDesignHuddlePublish } from "./use-design-huddle-publish-callback";
import { getProjectData } from "./utils";

const isTextElement = (
  element: ElementProperties,
): element is ElementProperties & { text: string } => {
  return Object.prototype.hasOwnProperty.call(element, "text");
};

export const usePublishDesignHuddleTemplateWithTags = (): ((
  projectId: string,
  onProgress?: (progress: number | null) => void,
) => Promise<DesignHuddleCustomisations | null>) => {
  const getToken = useGetDesignHuddleAccessToken();
  const publishTemplate = useDesignHuddlePublish();

  return useCallback(
    async (projectId: string, onProgress?: (progress: number | null) => void) => {
      const token = await getToken([projectId]);
      onProgress?.(40);
      const projectData = await getProjectData(projectId, token);
      onProgress?.(70);

      const mergeTags = projectData.pages
        ? projectData.pages.flatMap((page) =>
            extractMergeTags(
              Object.values(page.elements)
                .filter(isTextElement)
                .map((x) => x.text)
                .filter(notEmpty),
            ),
          )
        : [];

      if (mergeTags.length > 0) {
        const templateCode = await publishTemplate(projectId);
        onProgress?.(90);
        return { templateCode, mergeTags };
      }

      onProgress?.(null);
      return null;
    },
    [getToken, publishTemplate],
  );
};
